// Admin permissions (10-07) - dashboard copy of backend/services/adminPermissions.js.
//
// The server enforces every route with requireAccess; this copy only decides
// what to render. Keep REGISTRY in step with the backend when a card is added
// (GET /bff/admin/permissions/registry returns the server's version).

export const LEVELS = {
  alerts: ['none', 'read', 'edit', 'full'],
  review: ['none', 'preview', 'edit', 'full'],
};

export const REGISTRY = [
  {
    id: 'general',
    label: 'General',
    cards: [
      { id: 'overview', label: 'Overview stats' },
      { id: 'revenue', label: 'Revenue & subscriptions' },
      { id: 'dta', label: 'Desktop app (DTA)' },
      { id: 'growth', label: 'Growth & engagement' },
      { id: 'health', label: 'System health' },
    ],
  },
  {
    id: 'scanner',
    label: 'Scanner',
    cards: [
      { id: 'stats', label: 'Scanner stats' },
      { id: 'sourceHealth', label: 'Data source health' },
      { id: 'inspector', label: 'Domain inspector & re-rank' },
      { id: 'scoring', label: 'Scoring console' },
    ],
  },
  {
    id: 'alerts',
    label: 'Alerts',
    cards: [
      {
        id: 'alerts',
        label: 'Alerts',
        levels: 'alerts',
        levelLabels: { none: 'No access', read: 'Read', edit: 'Read & edit', full: 'Full access (incl. delete)' },
      },
      {
        id: 'review',
        label: 'Review queue',
        levels: 'review',
        levelLabels: { none: 'No access', preview: 'Preview', edit: 'Preview & edit', full: 'Full access (approve / reject)' },
      },
      { id: 'broadcast', label: 'Broadcast announcement' },
    ],
  },
  {
    id: 'intake',
    label: 'Intake',
    cards: [{ id: 'inbox', label: 'Intake inbox' }],
  },
  {
    id: 'users',
    label: 'Users & Access',
    cards: [
      { id: 'directory', label: 'Users directory' },
      { id: 'access', label: 'Admin access (grant, revoke, edit permissions)' },
    ],
  },
];

export const LEGACY_ADMIN_DEFAULT = {
  general: { full: true },
  scanner: { full: true },
  alerts: { full: false, cards: { alerts: 'edit', review: 'preview', broadcast: true } },
  intake: { full: true },
  users: { full: false, cards: { directory: true, access: false } },
};

const TAB_BY_ID = Object.fromEntries(REGISTRY.map((t) => [t.id, t]));
export const cardDef = (tab, card) => (TAB_BY_ID[tab] || { cards: [] }).cards.find((c) => c.id === card) || null;
const isLevelled = (def) => !!(def && def.levels);
const levelRank = (def, level) => (isLevelled(def) ? LEVELS[def.levels].indexOf(level) : -1);
export const topLevel = (def) => (isLevelled(def) ? LEVELS[def.levels][LEVELS[def.levels].length - 1] : true);

export function normalize(input) {
  const src = input && typeof input === 'object' ? input : {};
  const out = {};
  for (const tab of REGISTRY) {
    const t = src[tab.id] && typeof src[tab.id] === 'object' ? src[tab.id] : {};
    if (t.full === true) { out[tab.id] = { full: true }; continue; }
    const cards = {};
    const given = t.cards && typeof t.cards === 'object' ? t.cards : {};
    for (const c of tab.cards) {
      if (isLevelled(c)) {
        const v = given[c.id];
        cards[c.id] = LEVELS[c.levels].includes(v) ? v : 'none';
      } else {
        cards[c.id] = given[c.id] === true;
      }
    }
    out[tab.id] = { full: false, cards };
  }
  return out;
}

/** Level (or boolean) a map grants on a card, with tab-level full expanded. */
export function grantOf(perms, tab, card) {
  const def = cardDef(tab, card);
  const t = perms && perms[tab];
  if (!def || !t) return isLevelled(def) ? 'none' : false;
  if (t.full === true) return topLevel(def);
  const v = t.cards ? t.cards[card] : undefined;
  return isLevelled(def) ? (LEVELS[def.levels].includes(v) ? v : 'none') : v === true;
}

export function can(perms, tab, card, level) {
  const def = cardDef(tab, card);
  if (!def) return false;
  const v = grantOf(perms, tab, card);
  if (!isLevelled(def)) return v === true;
  const want = level || LEVELS[def.levels][1];
  return levelRank(def, v) >= levelRank(def, want) && levelRank(def, v) > 0;
}

export function anyAccess(perms) {
  return REGISTRY.some((tab) => tab.cards.some((c) => can(perms, tab.id, c.id)));
}

export function visibleTabs(perms) {
  return REGISTRY.filter((tab) => tab.cards.some((c) => can(perms, tab.id, c.id))).map((t) => t.id);
}

/** One line per tab, for the admin list: "Alerts: edit · review preview · broadcast". */
export function summarize(perms) {
  return REGISTRY.map((tab) => {
    const t = perms?.[tab.id];
    if (!t) return null;
    if (t.full) return `${tab.label}: full`;
    const parts = tab.cards.map((c) => {
      const v = grantOf(perms, tab.id, c.id);
      if (c.levels) return v === 'none' ? null : `${c.label.toLowerCase()} ${v}`;
      return v ? c.label.toLowerCase() : null;
    }).filter(Boolean);
    return parts.length ? `${tab.label}: ${parts.join(', ')}` : null;
  }).filter(Boolean);
}

const FULL_MAP = normalize(Object.fromEntries(REGISTRY.map((t) => [t.id, { full: true }])));
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/**
 * The access label shown wherever a role used to be: 'Full access' (elevated,
 * or an admin with every tab on full), 'Standard admin' (the preset bundle),
 * or 'Custom' (anything else). Plain users get null.
 */
export function accessLabel(role, perms) {
  if (role === 'superadmin') return 'Full access';
  if (role !== 'admin') return null;
  const p = normalize(perms || LEGACY_ADMIN_DEFAULT);
  if (same(p, FULL_MAP)) return 'Full access';
  if (same(p, normalize(LEGACY_ADMIN_DEFAULT))) return 'Standard admin';
  return 'Custom';
}

/**
 * Per-tab detail for the expandable view in Admin Access:
 * [{ tab, label, full, cards: [{ label, value }] }] where value is the level
 * label for a levelled card, or 'Yes' / 'No' for a binary one.
 */
export function describe(perms) {
  const p = normalize(perms);
  return REGISTRY.map((tab) => ({
    tab: tab.id,
    label: tab.label,
    full: p[tab.id].full,
    cards: tab.cards.map((c) => {
      const v = grantOf(p, tab.id, c.id);
      return { label: c.label, value: c.levels ? c.levelLabels[v] : (v ? 'Yes' : 'No'), granted: c.levels ? v !== 'none' : v === true };
    }),
  }));
}
